<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <p v-if="ticket" class="page-subtitle">
          <code>{{ ticket.ticketNumber }}</code>
          <span :class="['badge', statusBadgeClass(ticket.status)]">{{ statusLabel(ticket.status) }}</span>
        </p>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'SupportTickets' }" class="btn btn-secondary">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </router-link>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchTicket">重試</button>
      </div>

      <template v-else-if="ticket">
        <div class="detail-grid">
          <div class="detail-main">
            <section class="detail-card">
              <h2 class="section-title">工單內容</h2>
              <div class="form-group">
                <label class="form-label">主題</label>
                <input v-model="form.subject" type="text" class="form-control" maxlength="200" />
              </div>
              <div class="form-group">
                <label class="form-label">說明</label>
                <textarea v-model="form.description" class="form-control" rows="5" />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">分類</label>
                  <select v-model="form.categoryId" class="form-control">
                    <option value="">不指定</option>
                    <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">
                      {{ cat.name }}{{ cat.isActive ? '' : '（已停用）' }}
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">優先級</label>
                  <select v-model="form.priority" class="form-control">
                    <option value="low">低</option>
                    <option value="medium">中</option>
                    <option value="high">高</option>
                    <option value="urgent">緊急</option>
                  </select>
                </div>
              </div>
              <div class="form-actions">
                <button class="btn btn-primary" :disabled="saving" @click="handleSave">
                  <i class="ri-save-line"></i>
                  {{ saving ? '儲存中...' : '儲存變更' }}
                </button>
              </div>
            </section>

            <section class="detail-card">
              <h2 class="section-title">處理歷程</h2>

              <div class="history-compose">
                <textarea
                  v-model="historyDraft"
                  class="form-control"
                  rows="3"
                  placeholder="新增處理備註（例如：已聯絡客戶、等待回覆…）"
                  maxlength="5000"
                />
                <div class="history-compose-actions">
                  <button
                    class="btn btn-primary"
                    :disabled="addingHistory || !historyDraft.trim()"
                    @click="handleAddHistory"
                  >
                    <i class="ri-add-line"></i>
                    {{ addingHistory ? '新增中...' : '新增歷程' }}
                  </button>
                </div>
              </div>

              <div v-if="!histories.length" class="history-empty">尚無處理歷程</div>
              <ul v-else class="history-timeline">
                <li v-for="item in histories" :key="item.id" class="history-item">
                  <div class="history-dot" :class="`event-${item.eventType}`"></div>
                  <div class="history-body">
                    <div class="history-meta">
                      <span :class="['badge', eventBadgeClass(item.eventType)]">
                        {{ eventLabel(item.eventType) }}
                      </span>
                      <span class="history-time">{{ formatDate(item.createdAt) }}</span>
                      <span class="history-author">{{ item.createdByName || '系統' }}</span>
                    </div>
                    <p class="history-content">{{ item.content }}</p>
                  </div>
                </li>
              </ul>
            </section>
          </div>

          <aside class="detail-card meta-card">
            <h2 class="section-title">狀態與指派</h2>

            <div class="form-group">
              <label class="form-label">負責人</label>
              <div class="assign-row">
                <select v-model="assignedDraft" class="form-control">
                  <option value="">未指派</option>
                  <option v-if="currentUserId" :value="currentUserId">我自己</option>
                  <option
                    v-for="account in staffOptions"
                    :key="account.id"
                    :value="account.id"
                  >
                    {{ account.name || account.username }}
                  </option>
                </select>
                <button
                  class="btn btn-secondary"
                  :disabled="assigning || assignedDraft === (ticket.assignedTo || '')"
                  @click="handleAssign"
                >
                  指派
                </button>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">更新狀態</label>
              <select v-model="statusDraft" class="form-control">
                <option value="open">待處理</option>
                <option value="in_progress">處理中</option>
                <option value="resolved">已解決</option>
                <option value="closed">已關閉</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">狀態備註（選填）</label>
              <textarea
                v-model="statusRemarks"
                class="form-control"
                rows="2"
                placeholder="變更狀態時一併寫入歷程"
                maxlength="500"
              />
            </div>
            <button
              class="btn btn-secondary btn-block"
              :disabled="updatingStatus || statusDraft === ticket.status"
              @click="handleStatusUpdate"
            >
              更新狀態
            </button>

            <dl class="meta-list">
              <div class="meta-item">
                <dt>會員</dt>
                <dd>
                  <template v-if="ticket.memberName">
                    {{ ticket.memberName }}
                    <span v-if="ticket.memberPhone" class="text-muted">（{{ ticket.memberPhone }}）</span>
                  </template>
                  <span v-else class="text-muted">未關聯</span>
                </dd>
              </div>
              <div class="meta-item">
                <dt>負責人</dt>
                <dd>{{ ticket.assignedToName || '未指派' }}</dd>
              </div>
              <div class="meta-item">
                <dt>來源</dt>
                <dd>{{ ticket.source || '-' }}</dd>
              </div>
              <div class="meta-item">
                <dt>頻道</dt>
                <dd>{{ ticket.channel || '-' }}</dd>
              </div>
              <div class="meta-item">
                <dt>建立時間</dt>
                <dd>{{ formatDate(ticket.createdAt) }}</dd>
              </div>
              <div class="meta-item">
                <dt>更新時間</dt>
                <dd>{{ formatDate(ticket.updatedAt) }}</dd>
              </div>
              <div v-if="ticket.resolvedAt" class="meta-item">
                <dt>解決時間</dt>
                <dd>{{ formatDate(ticket.resolvedAt) }}</dd>
              </div>
              <div v-if="ticket.closedAt" class="meta-item">
                <dt>關閉時間</dt>
                <dd>{{ formatDate(ticket.closedAt) }}</dd>
              </div>
              <div v-if="ticket.chatdeskSessionId" class="meta-item">
                <dt>ChatDesk</dt>
                <dd>
                  <router-link
                    :to="{ path: '/client/chatdesk', query: { sessionId: ticket.chatdeskSessionId } }"
                    class="chatdesk-link"
                  >
                    開啟工作台對話
                  </router-link>
                  <div class="text-muted session-id">{{ ticket.chatdeskSessionId }}</div>
                </dd>
              </div>
            </dl>
          </aside>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { supportService } from '@/api/modules/support.js';
import { staffAccountService } from '@/api/modules/staffAccounts.js';
import authService from '@/helpers/authservice/authService.js';
import { showError, showSuccess } from '@/utils/swal.js';

const STATUS_LABELS = {
  open: '待處理',
  in_progress: '處理中',
  resolved: '已解決',
  closed: '已關閉'
};

const EVENT_LABELS = {
  note: '備註',
  created: '建立',
  status_change: '狀態',
  assigned: '指派',
  updated: '更新'
};

export default {
  name: 'SupportTicketDetail',
  setup() {
    const route = useRoute();
    const { pageTitle } = usePageTitle('客服工單詳情');
    const ticket = ref(null);
    const histories = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const saving = ref(false);
    const updatingStatus = ref(false);
    const assigning = ref(false);
    const addingHistory = ref(false);
    const statusDraft = ref('open');
    const statusRemarks = ref('');
    const assignedDraft = ref('');
    const historyDraft = ref('');
    const staffAccounts = ref([]);
    const categories = ref([]);
    const form = reactive({
      subject: '',
      description: '',
      categoryId: '',
      priority: 'medium'
    });

    const currentUser = authService.getCurrentUser() || {};
    const currentUserId = computed(() => currentUser.id || currentUser.accountId || '');

    const staffOptions = computed(() => {
      const me = currentUserId.value;
      return (staffAccounts.value || []).filter((a) => a.id !== me);
    });

    const categoryOptions = computed(() => {
      const list = categories.value || [];
      const currentId = form.categoryId;
      if (currentId && !list.find((c) => c.id === currentId)) {
        return [
          ...list,
          {
            id: currentId,
            name: ticket.value?.categoryName || ticket.value?.category || '（已刪除分類）',
            isActive: false
          }
        ];
      }
      return list;
    });

    const statusLabel = (status) => STATUS_LABELS[status] || status || '-';
    const eventLabel = (type) => EVENT_LABELS[type] || type || '紀錄';

    const statusBadgeClass = (status) => {
      const map = {
        open: 'badge-warning',
        in_progress: 'badge-info',
        resolved: 'badge-success',
        closed: 'badge-secondary'
      };
      return map[status] || 'badge-secondary';
    };

    const eventBadgeClass = (type) => {
      const map = {
        note: 'badge-info',
        created: 'badge-success',
        status_change: 'badge-warning',
        assigned: 'badge-primary',
        updated: 'badge-secondary'
      };
      return map[type] || 'badge-secondary';
    };

    const formatDate = (value) => {
      if (!value) return '-';
      try {
        return new Date(value).toLocaleString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return value;
      }
    };

    const applyTicket = (data) => {
      ticket.value = data;
      histories.value = data.histories || data.messages || [];
      form.subject = data.subject || '';
      form.description = data.description || '';
      form.categoryId = data.categoryId || '';
      form.priority = data.priority || 'medium';
      statusDraft.value = data.status || 'open';
      assignedDraft.value = data.assignedTo || '';
    };

    const fetchStaff = async () => {
      try {
        const response = await staffAccountService.getAccounts({
          page: 1,
          pageSize: 100,
          status: 'active'
        });
        if (response?.success && response.data) {
          staffAccounts.value = response.data.items || [];
        }
      } catch (err) {
        console.warn('載入員工帳號失敗:', err);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await supportService.listTicketCategories();
        categories.value = response?.data || [];
      } catch (err) {
        console.warn('載入分類失敗:', err);
      }
    };

    const fetchTicket = async () => {
      loading.value = true;
      error.value = null;
      try {
        const response = await supportService.getTicket(route.params.id);
        if (response?.success && response.data) {
          applyTicket(response.data);
        } else {
          error.value = response?.message || '獲取工單詳情失敗';
        }
      } catch (err) {
        console.error('獲取工單詳情失敗:', err);
        error.value = err.message || '獲取工單詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSave = async () => {
      if (!form.subject.trim() || !form.description.trim()) {
        await showError('請填寫必填欄位', '主題與說明為必填');
        return;
      }
      saving.value = true;
      try {
        const payload = {
          subject: form.subject.trim(),
          description: form.description.trim(),
          priority: form.priority,
          categoryId: form.categoryId || null
        };
        const response = await supportService.updateTicket(ticket.value.id, payload);
        if (response?.success && response.data) {
          applyTicket(response.data);
          await showSuccess('已儲存', '工單內容已更新');
        } else {
          await showError('儲存失敗', response?.message || '無法更新工單');
        }
      } catch (err) {
        console.error('更新工單失敗:', err);
        await showError('儲存失敗', err.message || '無法更新工單');
      } finally {
        saving.value = false;
      }
    };

    const handleStatusUpdate = async () => {
      updatingStatus.value = true;
      try {
        const response = await supportService.updateTicketStatus(
          ticket.value.id,
          statusDraft.value,
          statusRemarks.value.trim() || undefined
        );
        if (response?.success && response.data) {
          applyTicket(response.data);
          statusRemarks.value = '';
          await showSuccess('狀態已更新', statusLabel(statusDraft.value));
        } else {
          await showError('更新失敗', response?.message || '無法更新狀態');
        }
      } catch (err) {
        console.error('更新狀態失敗:', err);
        await showError('更新失敗', err.message || '無法更新狀態');
      } finally {
        updatingStatus.value = false;
      }
    };

    const handleAssign = async () => {
      assigning.value = true;
      try {
        const response = await supportService.updateTicket(ticket.value.id, {
          assignedTo: assignedDraft.value || null
        });
        if (response?.success && response.data) {
          applyTicket(response.data);
          await showSuccess('已更新', assignedDraft.value ? '已指派負責人' : '已取消指派');
        } else {
          await showError('指派失敗', response?.message || '無法更新負責人');
        }
      } catch (err) {
        console.error('指派失敗:', err);
        await showError('指派失敗', err.message || '無法更新負責人');
      } finally {
        assigning.value = false;
      }
    };

    const handleAddHistory = async () => {
      if (!historyDraft.value.trim()) return;
      addingHistory.value = true;
      try {
        const response = await supportService.addTicketHistory(ticket.value.id, {
          content: historyDraft.value.trim(),
          isInternal: true
        });
        if (response?.success) {
          historyDraft.value = '';
          await fetchTicket();
          await showSuccess('已新增', '處理歷程已記錄');
        } else {
          await showError('新增失敗', response?.message || '無法新增歷程');
        }
      } catch (err) {
        console.error('新增歷程失敗:', err);
        await showError('新增失敗', err.message || '無法新增歷程');
      } finally {
        addingHistory.value = false;
      }
    };

    onMounted(async () => {
      await Promise.all([fetchTicket(), fetchStaff(), fetchCategories()]);
    });

    return {
      pageTitle,
      ticket,
      histories,
      loading,
      error,
      form,
      saving,
      updatingStatus,
      assigning,
      addingHistory,
      statusDraft,
      statusRemarks,
      assignedDraft,
      historyDraft,
      staffOptions,
      categoryOptions,
      currentUserId,
      fetchTicket,
      handleSave,
      handleStatusUpdate,
      handleAssign,
      handleAddHistory,
      statusLabel,
      eventLabel,
      statusBadgeClass,
      eventBadgeClass,
      formatDate
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 0;
  font-size: 13px;
  color: #495057;
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;
}

.detail-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.detail-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.assign-row {
  display: flex;
  gap: 8px;
}

.btn-block {
  width: 100%;
  margin-bottom: 8px;
}

.meta-list {
  margin: 16px 0 0;
}

.meta-item {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 8px;
  padding: 10px 0;
  border-top: 1px solid #f1f3f5;
  font-size: 13px;
}

.meta-item dt {
  color: #868e96;
  margin: 0;
}

.meta-item dd {
  margin: 0;
  word-break: break-all;
}

.text-muted {
  color: #868e96;
}

.chatdesk-link {
  color: #1971c2;
  text-decoration: none;
  font-weight: 500;
}

.chatdesk-link:hover {
  text-decoration: underline;
}

.session-id {
  font-size: 11px;
  margin-top: 4px;
  word-break: break-all;
}

.history-compose {
  margin-bottom: 20px;
}

.history-compose-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.history-empty {
  color: #868e96;
  font-size: 14px;
  padding: 12px 0;
}

.history-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  border-left: 2px solid #e9ecef;
  margin-left: 6px;
}

.history-item {
  position: relative;
  padding: 0 0 18px 18px;
}

.history-dot {
  position: absolute;
  left: -7px;
  top: 6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #adb5bd;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #e9ecef;
}

.history-dot.event-created { background: #40c057; }
.history-dot.event-status_change { background: #fab005; }
.history-dot.event-assigned { background: #228be6; }
.history-dot.event-note { background: #15aabf; }
.history-dot.event-updated { background: #868e96; }

.history-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #868e96;
}

.history-content {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.badge-primary {
  background: #228be6;
  color: #fff;
}

@media (max-width: 960px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .assign-row {
    flex-direction: column;
  }
}
</style>
